/* eslint-disable react/jsx-one-expression-per-line */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-nested-ternary */

import { Button, HStack, Menu, MenuButton, MenuList, MenuItem, Text, Tooltip, useTheme as useChakraTheme } from '@chakra-ui/core';
import React, { FC, useCallback, useMemo } from 'react';
import isObject from 'lodash/isObject';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from '../../../../models/hooks';
import { translateFullFormSchema } from './utils';
import LanguageMenuItemContent from './LanguageMenuItemContent';
import { FormItemT } from '../types';
import { FORM_COMPONENT_ADVANCED_INPUT_TYPE } from '../schemas/formComponentInputTypeSchema';
import { FORM_ADVANCED_SECTIONS } from '../schemas/formAdvancedSections';
import { ComponentInputTypeT } from '../schemas/types';

export const languages = ['en', 'fr'] as const;

export const defaultAvailableLanguages: {
  label: string;
  value: string;
}[] = [
  { label: 'English (En)', value: 'en' },
  { label: 'French (Fr)', value: 'fr' },
];

export type Language = typeof languages[number];

const QUESTION_ADVANCED_MAP: Record<string, ComponentInputTypeT> = {
  [FORM_ADVANCED_SECTIONS.sin]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.sinAdvanced,
  [FORM_ADVANCED_SECTIONS.ssn]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.ssnAdvanced,
  [FORM_ADVANCED_SECTIONS.canadianBank]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.canadianBank,
  [FORM_ADVANCED_SECTIONS.americanBank]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.americanBank,
  [FORM_ADVANCED_SECTIONS.addressValidator]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.addressValidator,
  [FORM_ADVANCED_SECTIONS.emergencyContact]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.emergencyContact,
  [FORM_ADVANCED_SECTIONS.fullName]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.fullName,
  [FORM_ADVANCED_SECTIONS.signature]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.signatureAdvanced,
  [FORM_ADVANCED_SECTIONS.documentPreview]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.documentPreview,
  [FORM_ADVANCED_SECTIONS.earliestStartDate]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.earliestStartDate,
  [FORM_ADVANCED_SECTIONS.miscellaneous]: FORM_COMPONENT_ADVANCED_INPUT_TYPE.miscellaneous,
};

const SelectLanguage: FC = () => {
  const { t } = useTranslation('hrFormTemplate');
  const chakraTheme = useChakraTheme();

  const { validationErrors: errors = [] } = useStoreState((state) => state.formBuilder);
  const formSchema = useStoreState((state) => state.formBuilder.formSchema);
  const currentLanguage = useStoreState((state) => state.formBuilder.currentLanguage);
  const availableLanguages = useStoreState((state) => state.formBuilder.availableLanguages);
  const defaultLanguage = useStoreState((state) => {
    return state.formBuilder.defaultLanguage;
  });
  const supportedLanguagesStore = useStoreState((s) => s.app.accounts?.configuration?.supportedLanguages);

  const setCurrentLanguage = useStoreActions((actions) => actions.formBuilder.setCurrentLanguage);
  const setAvailableLanguages = useStoreActions((actions) => actions.formBuilder.setAvailableLanguages);
  const setDefaultLanguage = useStoreActions((actions) => actions.formBuilder.setDefaultLanguage);
  const setSchema = useStoreActions((actions) => actions.formBuilder.setSchema);

  const supportedLanguages = useMemo(() => supportedLanguagesStore ?? defaultAvailableLanguages, [supportedLanguagesStore]);

  const sortedLanguages = useMemo(() => {
    const languagesArray = Array.isArray(supportedLanguages)
      ? supportedLanguages
      : isObject(supportedLanguages)
      ? Object.entries(supportedLanguages).map(([value, label]) => ({ value, label: String(label) }))
      : [];

    return [...languagesArray].sort((a, b) =>
      a.value === defaultLanguage ? -1 : b.value === defaultLanguage ? 1 : a.label.localeCompare(b.label),
    );
  }, [defaultLanguage, supportedLanguages]);

  React.useEffect(() => {
    if (!errors?.length) return;

    const firstError = errors[0];
    if (!firstError?.innerPath) return;

    const pathSegments = firstError.innerPath.split('.');
    const errorLanguage = pathSegments[pathSegments.length - 1];

    const isSupportedLanguage = availableLanguages.includes(errorLanguage);
    if (!isSupportedLanguage) return;

    const shouldSwitchLanguage = currentLanguage !== errorLanguage;
    if (shouldSwitchLanguage) {
      setCurrentLanguage(errorLanguage);
    }
  }, [errors]);

  const handleLanguageChange = useCallback(
    (langValue: string) => {
      setCurrentLanguage(langValue as Language);
    },
    [setCurrentLanguage],
  );

  const handleAddLanguage = useCallback(
    (langValue: string) => {
      const newLang = langValue as Language;
      if (availableLanguages.includes(newLang)) return;

      const newActiveLanguages = [...availableLanguages, newLang];
      setCurrentLanguage(newLang);
      setAvailableLanguages(newActiveLanguages);

      const translatedSchema: FormItemT[] = translateFullFormSchema(
        formSchema,
        newLang,
        defaultLanguage,
        newActiveLanguages,
        QUESTION_ADVANCED_MAP,
      );
      setSchema(translatedSchema ?? []);
    },
    [availableLanguages, formSchema, defaultLanguage, setCurrentLanguage, setAvailableLanguages, setSchema],
  );

  const handleRemoveLanguage = useCallback(
    (langValue: string) => {
      if (availableLanguages.length <= 1 || langValue === defaultLanguage) return;

      const newActiveLanguages = availableLanguages.filter((l) => l !== langValue);
      setAvailableLanguages(newActiveLanguages);

      if (currentLanguage === langValue) {
        setCurrentLanguage(defaultLanguage as Language);
      }
    },
    [setAvailableLanguages, setCurrentLanguage, availableLanguages, currentLanguage, defaultLanguage],
  );

  const handleSetDefaultLanguage = useCallback(
    (langValue: string) => {
      if (availableLanguages.includes(langValue)) {
        setDefaultLanguage(langValue as Language);
      } else {
        console.warn(`Attempted to set non-active language "${langValue}" as default.`);
      }
    },
    [setDefaultLanguage, availableLanguages],
  );

  if (sortedLanguages.length === 0) {
    return <Text fontSize="sm">{t('formBuilder.languageSelectMenu.noSupportedLanguages') || 'No languages configured.'}</Text>;
  }

  const isSingleLanguageOnly = sortedLanguages.length <= 1 && availableLanguages.includes(sortedLanguages[0]?.value);

  const tooltipLabel = isSingleLanguageOnly
    ? t('formBuilder.languageSelectMenu.onlyOneLanguage')
    : t('formBuilder.languageSelectMenu.chooseLanguage');

  return (
    <Menu closeOnSelect={false}>
      <Tooltip label={tooltipLabel} placement="bottom" shouldWrapChildren hasArrow>
        <MenuButton
          as={Button as any}
          rightIcon={isSingleLanguageOnly ? undefined : <ChevronDownIcon />}
          data-testid="select-language-menu-button"
          isDisabled={isSingleLanguageOnly}
          _disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
          size="sm"
          variant="outline"
        >
          <HStack spacing={1}>
            <Text textTransform="capitalize" fontWeight="medium">
              {currentLanguage}
            </Text>
            {currentLanguage === defaultLanguage && (
              <Text fontSize="xs" fontWeight="normal" color="gray.500">
                ({t('formBuilder.languageSelectMenu.primary')})
              </Text>
            )}
          </HStack>
        </MenuButton>
      </Tooltip>

      <MenuList minW="240px" zIndex="popover">
        {sortedLanguages.map(({ label, value: langValue }) => {
          const isDefault = langValue === defaultLanguage;
          const isAvailableInForm = availableLanguages.includes(langValue);
          const isCurrentlySelected = currentLanguage === langValue;
          const canRemove = !isDefault && isAvailableInForm && availableLanguages.length > 1;
          const canAdd = !isAvailableInForm;
          const canSetDefault = isAvailableInForm && !isDefault;

          return (
            <MenuItem
              key={langValue}
              onClick={() => {
                if (isAvailableInForm && !isCurrentlySelected) {
                  handleLanguageChange(langValue);
                }
              }}
              bg={isCurrentlySelected ? chakraTheme.colors.blue[50] : 'transparent'}
              _hover={{ bg: !isCurrentlySelected ? chakraTheme.colors.gray[50] : undefined }}
              data-testid={`select-language-menu-item-${langValue}`}
              closeOnSelect={false}
              paddingRight={2}
            >
              <LanguageMenuItemContent
                langValue={langValue}
                label={label}
                isDefault={isDefault}
                isAvailableInForm={isAvailableInForm}
                isCurrentlySelected={isCurrentlySelected}
                canSetDefault={canSetDefault}
                canAdd={canAdd}
                canRemove={canRemove}
                onSetDefault={handleSetDefaultLanguage}
                onAdd={handleAddLanguage}
                onRemove={handleRemoveLanguage}
                onSelectLanguage={handleLanguageChange}
              />
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
};

export default SelectLanguage;
